/**
 * Screen Wake Lock: mantiene la pantalla encendida durante el recorrido.
 *
 * Sin esto, la pantalla se apaga al minuto, el navegador suspende la pagina y
 * watchPosition deja de entregar posiciones. El resultado seria que la persona
 * llega al punto y no pasa nada, que es el fallo mas frustrante posible en este
 * tipo de juego.
 *
 * La API no esta en todos los navegadores. Cuando falta, no se rompe nada: se
 * informa en la interfaz de que conviene no bloquear el movil.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface Resultado {
  soportado: boolean;
  activo: boolean;
  activar: () => Promise<void>;
  liberar: () => Promise<void>;
}

export function usePantallaEncendida(): Resultado {
  const soportado = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  const bloqueo = useRef<WakeLockSentinel | null>(null);
  const quiereActivo = useRef(false);
  const [activo, setActivo] = useState(false);

  const activar = useCallback(async () => {
    if (!soportado) return;
    quiereActivo.current = true;
    try {
      bloqueo.current = await navigator.wakeLock.request('screen');
      setActivo(true);
      bloqueo.current.addEventListener('release', () => {
        setActivo(false);
      });
    } catch {
      // El navegador puede rechazarlo con la bateria baja. No es un error que
      // deba interrumpir el recorrido.
      setActivo(false);
    }
  }, [soportado]);

  const liberar = useCallback(async () => {
    quiereActivo.current = false;
    if (bloqueo.current) {
      try {
        await bloqueo.current.release();
      } catch {
        // Si ya estaba liberado, no hay nada que hacer.
      }
      bloqueo.current = null;
    }
    setActivo(false);
  }, []);

  /** El bloqueo se pierde al pasar a segundo plano y hay que volver a pedirlo. */
  useEffect(() => {
    function alVolver(): void {
      if (document.visibilityState === 'visible' && quiereActivo.current) {
        void activar();
      }
    }
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [activar]);

  useEffect(() => {
    return () => {
      if (bloqueo.current) void bloqueo.current.release().catch(() => undefined);
    };
  }, []);

  return { soportado, activo, activar, liberar };
}
